import React, { useState, useEffect, useRef } from 'react';
import { Play, Clock, X } from 'lucide-react';

/**
 * CompactVideoStrip — Minimum-format horizontal strip of video thumbnails.
 *
 * Meant for the homepage and calculator-page footers where the full
 * VideoShelf would waste vertical space. Click any thumb to open the
 * lesson in a lightweight overlay player (single iframe, no navigation
 * away). Same YouTube-nocookie query params as VideoShelf.
 *
 * Props:
 *   title?     — small heading (default "Watch")
 *   subtitle?  — optional one-liner
 *   videos     — array of {videoId, title, duration, search?}
 *   testid?    — data-testid prefix (default "compact-video-strip")
 *   ctaLabel?  — optional "See all lessons" link label
 *   onCtaClick?— optional callback for CTA
 */
export const CompactVideoStrip = ({
  title = 'Watch',
  subtitle,
  videos = [],
  testid = 'compact-video-strip',
  ctaLabel,
  onCtaClick,
}) => {
  const [active, setActive] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (active === null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the dialog so keydown lands on it — YouTube iframe would
    // otherwise capture focus and swallow the Escape key.
    // Re-focus every 400ms while the modal is open, to reclaim focus if
    // the iframe steals it after loading.
    const focusDialog = () => { dialogRef.current?.focus(); };
    focusDialog();
    const focusInt = setInterval(focusDialog, 500);
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    // Listen on window too as a belt-and-braces fallback.
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearInterval(focusInt);
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [active]);

  if (!videos || videos.length === 0) return null;

  const embedUrl = (v) => {
    if (v.videoId) {
      return `https://www.youtube-nocookie.com/embed/${v.videoId}?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&autoplay=1&fs=1`;
    }
    const q = encodeURIComponent(v.search || v.title);
    return `https://www.youtube-nocookie.com/embed?listType=search&list=${q}&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&fs=1`;
  };
  const posterUrl = (v) => (v.videoId ? `https://i.ytimg.com/vi/${v.videoId}/mqdefault.jpg` : null);

  return (
    <section data-testid={testid} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="min-w-0">
          <h3 className="font-heading text-[13px] md:text-[14px] font-semibold text-[#0D1520] leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[11px] text-stone-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        {ctaLabel && (
          <button
            type="button"
            onClick={onCtaClick}
            data-testid={`${testid}-cta`}
            className="text-[11.5px] font-semibold text-[#0D1520] hover:text-[#0D1520] hover:underline shrink-0"
          >
            {ctaLabel} →
          </button>
        )}
      </div>

      <ul className="grid grid-cols-3 gap-2 md:gap-3">
        {videos.slice(0, 3).map((v, idx) => (
          <li key={v.videoId || `slot-${idx}`}>
            <button
              type="button"
              onClick={() => setActive(idx)}
              data-testid={`${testid}-thumb-${idx}`}
              className="group relative w-full aspect-video overflow-hidden rounded-lg bg-[#EDF5F1] border border-[#E2DCD5] hover:border-gold-500/50 transition-all"
              aria-label={`Play: ${v.title}`}
            >
              {posterUrl(v) ? (
                <img
                  src={posterUrl(v)}
                  alt={v.title ? `Video thumbnail — ${v.title}` : ''}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#EDF5F1] to-[#EDF5F1]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#EDF5F1] via-[#EDF5F1] to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gold-500/95 text-[#0D1520] shadow-md group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                </span>
              </div>
              {v.duration && (
                <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 rounded-full bg-[rgba(43,36,25,0.35)] text-[#0D1520] text-[9px] font-mono px-1.5 py-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {v.duration}
                </span>
              )}
              <span className="absolute left-1.5 bottom-1.5 right-10 text-[#0D1520] text-[10.5px] font-semibold leading-tight line-clamp-2 text-left drop-shadow">
                {v.title}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/* Lightweight overlay player */}
      {active !== null && videos[active] && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={videos[active].title}
          data-testid={`${testid}-player`}
          tabIndex={-1}
          onKeyDown={(e) => { if (e.key === 'Escape') setActive(null); }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(43,36,25,0.55)] backdrop-blur-sm p-3 focus:outline-none"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-3xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid={`${testid}-close`}
              className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[rgba(43,36,25,0.35)] text-[#0D1520] hover:bg-black transition-colors"
              aria-label="Close video"
            >
              <X className="w-4 h-4" />
            </button>
            <iframe
              title={videos[active].title}
              src={embedUrl(videos[active])}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default CompactVideoStrip;
